// File: TradeDetail.js
// ✅ FULL DROP-IN (STRICT CASH ONLY UI MODE)
// ✅ Premium offer card refresh
// ✅ Single offer CTA only
// ✅ Animated offer count on successful return from offer page
// ✅ "Offers Made" stat highlight pulse only
// ✅ Refined micro-banner after offer submission
// ✅ Keeps existing workflow, public-first loading, similar trades, charts, and gating intact

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import "./TradeDetail.css";
import { useAuth } from "../contexts/AuthContext";

import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  serverTimestamp,
  query,
  where,
  limit,
  onSnapshot,
  orderBy,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import TradeWorkflowPanel from "./TradeWorkflowPanel";
import TradePerformanceChart from "./TradePerformanceChart";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import SimilarTradesCarousel from "./SimilarTradesCarousel";

import {
  getTradeImageSrc,
  handleImgError,
  extractImageCandidates,
  isBadImageUrl,
} from "../utils/images";

/* =========================================================
   STRICT CASH ONLY UI FLAGS
========================================================= */
const CASH_ONLY_UI = true;
const HIDE_SIMILAR_ITEMS = false;

/* -------------------- ROUTING -------------------- */
const OFFER_ROUTE_WITH_PARAM = (tradeId) => `/trade/${tradeId}/offer`;

/* -------------------- HELPERS -------------------- */
const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    return 0;
  } catch {
    return 0;
  }
};

const normStr = (v) => String(v ?? "").trim();
const safeLower = (v) => String(v ?? "").trim().toLowerCase();

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const normalizeImages = (t) => {
  const val = t?.imageURLs ?? t?.imageUrls ?? t?.images ?? t?.imageURL ?? t?.imageUrl;
  const arr = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
  return arr
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .slice(0, 10);
};

const computePostedLabel = (trade) => {
  const ms = toMillisSafe(trade?.createdAt);
  if (!ms) return "Date not available";
  const days = Math.max(0, Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24)));
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

const isProbablyAvailable = (t) => {
  if (!t) return false;
  if (t.isAvailable === false) return false;
  const st = String(t.status || "").toLowerCase();
  if (st === "closed" || st === "sold") return false;
  if (t.inWorkflow === true) return false;
  return true;
};

const pickPrice = (t) => (t?.expectedCashAmount != null ? t.expectedCashAmount : t?.estimatedValue);

/* -------------------- WORKFLOW HELPERS -------------------- */
const normStage = (v) => (v == null ? "" : String(v).trim());

const WF_UI = {
  DELIVERED_PENDING_VERIFICATION: "delivered_pending_buyer_verification",
  DISPUTE_OPEN: "dispute_open",
  COMPLETED: "completed",
};

const IN_PROCESS_STAGES = new Set([
  "buyer_approval",
  "awaiting_payment",
  "ready_for_inspection",
  "awaiting_dropoff",
  "inspection_passed",
  "pickup_scheduled",
  "pickup_in_progress",
  "hub_inspection_pass",
  "delivery_scheduled",
  "delivery_in_progress",
  "delivered_pending_buyer_verification",
  "verification_passed",
  "completed",
  "cancelled",
]);

const prettyStage = (stage) => {
  const s = normStr(stage);
  if (!s) return "";
  return s.replace(/_/g, " ");
};

const getWorkflowBannerText = (workflow, loadingWorkflow) => {
  if (loadingWorkflow) return " Loading status…";

  const stage = safeLower(workflow?.stage || "");
  if (!stage) return "";

  const method = safeLower(workflow?.fulfillmentChoice?.method || "");
  const dropoffPartnerId = safeLower(workflow?.dropoff?.partnerId || "");
  const isPartnerDropoff =
    method === "partner_dropoff" ||
    dropoffPartnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff";

  const routeLabel = isPartnerDropoff
    ? "approved drop-off point"
    : method === "platform_pickup"
    ? "platform inspection and pickup"
    : "";

  return ` Stage: ${prettyStage(stage)}${routeLabel ? ` • Route: ${routeLabel}` : ""}`;
};

const animateCount = (from, to, setValue, duration = 850) => {
  const start = performance.now();
  const safeFrom = Number.isFinite(from) ? from : 0;
  const safeTo = Number.isFinite(to) ? to : 0;

  if (safeFrom === safeTo) {
    setValue(safeTo);
    return () => {};
  }

  let raf = 0;

  const step = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const next = Math.round(safeFrom + (safeTo - safeFrom) * eased);
    setValue(next);

    if (progress < 1) {
      raf = requestAnimationFrame(step);
    }
  };

  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
};

/* -------------------- COMPONENT -------------------- */
const TradeDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const workflowSectionRef = useRef(null);
  const similarRef = useRef(null);
  const offerReturnHandledRef = useRef(false);

  const focusWorkflow = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search || "");
      const tab = (qs.get("tab") || "").toLowerCase();
      return tab === "workflow";
    } catch {
      return false;
    }
  }, [location.search]);

  const [trade, setTrade] = useState(null);
  const [mainImageIndex, setMainImageIndex] = useState(0);

  const [isFavorited, setIsFavorited] = useState(false);
  const [showThanksBanner, setShowThanksBanner] = useState(false);

  const [similarTrades, setSimilarTrades] = useState([]);

  const [workflow, setWorkflow] = useState(null);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);

  const [loadState, setLoadState] = useState({
    loading: true,
    mode: "public",
    notFound: false,
    accessDenied: false,
  });

  const [localOfferCount, setLocalOfferCount] = useState(0);
  const [displayOfferCount, setDisplayOfferCount] = useState(0);
  const [offerCountPulse, setOfferCountPulse] = useState(false);
  const [offerMicroBanner, setOfferMicroBanner] = useState({ show: false, text: "" });

  const expectedCash = useMemo(() => {
    return trade?.expectedCashAmount != null ? trade.expectedCashAmount : trade?.estimatedValue;
  }, [trade]);

  const sessionUid = normStr(currentUser?.uid);
  const sessionEmail = safeLower(currentUser?.email || "");

  const isAdmin = useMemo(() => {
    return (
      !!currentUser?.email &&
      !!currentUser?.emailVerified &&
      safeLower(currentUser.email).endsWith("@nexterzone.com")
    );
  }, [currentUser?.email, currentUser?.emailVerified]);

  const isOwner = useMemo(() => {
    const me = normStr(currentUser?.uid);
    const owner = normStr(trade?.ownerId || trade?.ownerUid || trade?.sellerUid);
    return !!me && !!owner && me === owner;
  }, [currentUser?.uid, trade?.ownerId, trade?.ownerUid, trade?.sellerUid]);

  const isBuyer = useMemo(() => {
    const buyerUid = normStr(workflow?.buyerUid || trade?.buyerUid);
    return !!sessionUid && !!buyerUid && buyerUid === sessionUid;
  }, [workflow?.buyerUid, trade?.buyerUid, sessionUid]);

  const isSellerParty = useMemo(() => {
    const sellerUid = normStr(workflow?.sellerUid || trade?.ownerId || trade?.ownerUid || trade?.sellerUid);
    return !!sessionUid && !!sellerUid && sellerUid === sessionUid;
  }, [workflow?.sellerUid, trade?.ownerId, trade?.ownerUid, trade?.sellerUid, sessionUid]);

  const canPartyView = useMemo(() => isAdmin || isBuyer || isSellerParty, [isAdmin, isBuyer, isSellerParty]);

  const safeImages = useMemo(() => {
    const arr = extractImageCandidates(trade).filter((u) => !isBadImageUrl(u));
    return arr.length ? arr : [getTradeImageSrc(trade)];
  }, [trade]);

  /* -------------------- USER PREFS / HISTORY -------------------- */
  const fetchPreferences = useCallback(
    async (uid) => {
      try {
        const prefRef = doc(db, "preferences", uid);
        const prefSnap = await getDoc(prefRef);
        if (prefSnap.exists()) {
          const data = prefSnap.data() || {};
          setIsFavorited((data.favorites || []).includes(id));
        } else {
          setIsFavorited(false);
        }
      } catch (err) {
        console.error("❌ Failed to load preferences:", err);
      }
    },
    [id]
  );

  const trackViewedTrade = useCallback(async (uid, tradeId) => {
    const uidNorm = String(uid || "").trim();
    const tradeIdNorm = String(tradeId || "").trim();
    if (!uidNorm || !tradeIdNorm) return;

    try {
      const prefRef = doc(db, "preferences", uidNorm);

      let prefData = {};
      try {
        const prefSnap = await getDoc(prefRef);
        prefData = prefSnap.exists() ? prefSnap.data() || {} : {};
      } catch (e) {
        console.warn("[trackViewedTrade] preferences read failed:", e?.code || e?.message || e);
        return;
      }

      const history = Array.isArray(prefData.recentViews) ? prefData.recentViews : [];
      const alreadyInHistory = history.includes(tradeIdNorm);

      const updatedHistory = [tradeIdNorm, ...history.filter((tid) => tid !== tradeIdNorm)].slice(0, 10);
      const historyChanged =
        history.length !== updatedHistory.length || history.some((v, i) => v !== updatedHistory[i]);

      if (historyChanged) {
        await setDoc(prefRef, { ...prefData, recentViews: updatedHistory }, { merge: true });
      }

      if (!alreadyInHistory) {
        const viewRef = doc(db, "users", uidNorm);
        await setDoc(
          viewRef,
          {
            lastViewedTradeId: tradeIdNorm,
            lastViewedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
    } catch (error) {
      console.error("❌ Failed to track view:", error?.code || error?.message || error);
    }
  }, []);

  /* -------------------- LOAD TRADE (PUBLIC THEN PARTY FALLBACK) -------------------- */
  useEffect(() => {
    let cancelled = false;

    const fetchTrade = async () => {
      setLoadState({ loading: true, mode: "public", notFound: false, accessDenied: false });
      setTrade(null);
      setWorkflow(null);

      try {
        if (!id) {
          setLoadState({ loading: false, mode: "public", notFound: true, accessDenied: false });
          return;
        }

        const pubRef = doc(db, "PublicTrades", id);
        const pubSnap = await getDoc(pubRef);

        if (pubSnap.exists()) {
          const publicData = pubSnap.data() || {};

          let canonicalData = null;
          if (currentUser?.uid && isAdmin) {
            try {
              const canonRef = doc(db, "Trades", id);
              const canonSnap = await getDoc(canonRef);
              if (canonSnap.exists()) canonicalData = canonSnap.data() || null;
            } catch {}
          }

          const merged = { ...(canonicalData || {}), ...publicData };
          const images = normalizeImages(merged);
          const ownerIdNorm = normStr(merged.ownerId || merged.ownerUid || merged.sellerUid) || null;

          const tradeWithId = {
            id: pubSnap.id,
            ...merged,
            imageURLs: images,
            ownerId: ownerIdNorm,
            ownerUid: normStr(merged.ownerUid || merged.ownerId || merged.sellerUid) || ownerIdNorm,
          };

          if (cancelled) return;

          setTrade(tradeWithId);
          setMainImageIndex(0);
          setLoadState({ loading: false, mode: "public", notFound: false, accessDenied: false });

          if (currentUser?.uid) {
            await fetchPreferences(currentUser.uid);
            await trackViewedTrade(currentUser.uid, pubSnap.id);
          } else {
            setIsFavorited(false);
          }
          return;
        }

        if (!currentUser?.uid) {
          if (!cancelled) {
            setLoadState({ loading: false, mode: "public", notFound: true, accessDenied: false });
            setTrade(null);
          }
          return;
        }

        let wfData = null;
        try {
          const wfRef = doc(db, "TradeWorkflows", id);
          const wfSnap = await getDoc(wfRef);
          wfData = wfSnap.exists() ? wfSnap.data() || {} : null;
        } catch {
          wfData = null;
        }

        const canonRef = doc(db, "Trades", id);
        const canonSnap = await getDoc(canonRef);
        if (!canonSnap.exists()) {
          if (!cancelled) {
            setLoadState({ loading: false, mode: "party", notFound: true, accessDenied: false });
            setTrade(null);
            setWorkflow(wfData);
          }
          return;
        }

        const canonical = canonSnap.data() || {};
        const images = normalizeImages(canonical);
        const ownerIdNorm = normStr(canonical.ownerId || canonical.ownerUid || canonical.sellerUid) || null;

        const tradeWithId = {
          id: canonSnap.id,
          ...canonical,
          imageURLs: images,
          ownerId: ownerIdNorm,
          ownerUid: normStr(canonical.ownerUid || canonical.ownerId || canonical.sellerUid) || ownerIdNorm,
        };

        const buyerUid = normStr(wfData?.buyerUid || tradeWithId?.buyerUid);
        const sellerUid = normStr(
          wfData?.sellerUid || tradeWithId?.ownerId || tradeWithId?.ownerUid || tradeWithId?.sellerUid
        );

        const partyAllowed =
          isAdmin ||
          (buyerUid && buyerUid === sessionUid) ||
          (sellerUid && sellerUid === sessionUid) ||
          (!!canonical?.buyerEmail && safeLower(canonical.buyerEmail) === sessionEmail);

        if (!partyAllowed) {
          if (!cancelled) {
            setLoadState({ loading: false, mode: "party", notFound: false, accessDenied: true });
            setTrade(null);
            setWorkflow(wfData);
          }
          return;
        }

        if (cancelled) return;

        setTrade(tradeWithId);
        setWorkflow(wfData);
        setMainImageIndex(0);
        setLoadState({ loading: false, mode: "party", notFound: false, accessDenied: false });

        await fetchPreferences(currentUser.uid);
        await trackViewedTrade(currentUser.uid, tradeWithId.id);
      } catch (error) {
        console.error("❌ Error fetching trade:", error);
        if (!cancelled) {
          setTrade(null);
          setWorkflow(null);
          setLoadState({ loading: false, mode: "public", notFound: true, accessDenied: false });
        }
      }
    };

    fetchTrade();
    return () => {
      cancelled = true;
    };
  }, [id, currentUser?.uid, isAdmin, sessionUid, sessionEmail, fetchPreferences, trackViewedTrade]);

  /* -------------------- OFFER COUNT SYNC -------------------- */
  useEffect(() => {
    if (!trade) return;

    const count =
      Number(trade.offerCount) ||
      Number(trade.offersCount) ||
      Number(trade.totalOffers) ||
      0;

    setLocalOfferCount(count);
  }, [trade]);

  useEffect(() => {
    const stop = animateCount(displayOfferCount, localOfferCount, setDisplayOfferCount, 850);
    return stop;
  }, [localOfferCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!location.state?.offerJustCreated) return;
    if (offerReturnHandledRef.current) return;

    offerReturnHandledRef.current = true;

    setLocalOfferCount((prev) => prev + 1);
    setShowThanksBanner(true);
    setOfferMicroBanner({
      show: true,
      text: "New offer submitted successfully. The seller has been notified.",
    });
    setOfferCountPulse(true);

    const pulseTimer = setTimeout(() => setOfferCountPulse(false), 1400);
    const bannerTimer = setTimeout(() => setOfferMicroBanner({ show: false, text: "" }), 4200);
    const thanksTimer = setTimeout(() => setShowThanksBanner(false), 3200);

    navigate(`${location.pathname}${location.search || ""}`, { replace: true, state: {} });

    return () => {
      clearTimeout(pulseTimer);
      clearTimeout(bannerTimer);
      clearTimeout(thanksTimer);
    };
  }, [location.state, navigate, location.pathname, location.search]);

  /* -------------------- WORKFLOW LIVE SUBSCRIPTION -------------------- */
  useEffect(() => {
    if (!id) return;
    if (!currentUser?.uid) return;

    if (!isAdmin && loadState.mode !== "party") return;

    setLoadingWorkflow(true);

    const ref = doc(db, "TradeWorkflows", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setWorkflow(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoadingWorkflow(false);
      },
      (err) => {
        console.warn("[TradeDetail] workflow snapshot error:", err);
        setWorkflow(null);
        setLoadingWorkflow(false);
      }
    );

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [id, currentUser?.uid, isAdmin, loadState.mode]);

  useEffect(() => {
    if (!focusWorkflow) return;

    const t = setTimeout(() => {
      const el = workflowSectionRef.current || document.getElementById("workflow");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);

    return () => clearTimeout(t);
  }, [focusWorkflow, loadState.loading, workflow?.stage, trade?.id]);

  /* -------------------- Keep mainImageIndex valid -------------------- */
  useEffect(() => {
    const count = Array.isArray(safeImages) ? safeImages.length : 0;
    if (count <= 0) return;
    if (mainImageIndex >= count) setMainImageIndex(0);
  }, [safeImages, mainImageIndex]);

  /* -------------------- Similar trades -------------------- */
  useEffect(() => {
    if (HIDE_SIMILAR_ITEMS) {
      setSimilarTrades([]);
      return;
    }

    let cancelled = false;

    const fetchSimilar = async () => {
      const tradeId = normStr(trade?.id);
      if (!tradeId) return;

      const rawCategory = normStr(trade?.category || "");
      const categoryNorm = safeLower(rawCategory);
      const title = normStr(trade?.title || "");
      const firstWord = safeLower(title.split(/\s+/).filter(Boolean)[0] || "");

      const resultsMap = new Map();

      const pushDocs = (snap) => {
        snap.docs.forEach((d) => resultsMap.set(d.id, { id: d.id, ...d.data() }));
      };

      try {
        if (rawCategory) {
          try {
            const q1 = query(collection(db, "PublicTrades"), where("category", "==", rawCategory), limit(40));
            pushDocs(await getDocs(q1));
          } catch {}
        }

        if (resultsMap.size < 10 && categoryNorm) {
          try {
            const q2 = query(collection(db, "PublicTrades"), where("categoryNorm", "==", categoryNorm), limit(40));
            pushDocs(await getDocs(q2));
          } catch {}
        }

        if (resultsMap.size < 10 && firstWord) {
          try {
            const q3 = query(collection(db, "PublicTrades"), where("titleTokens", "array-contains", firstWord), limit(40));
            pushDocs(await getDocs(q3));
          } catch {}
        }

        if (resultsMap.size < 10) {
          try {
            const q4 = query(collection(db, "PublicTrades"), orderBy("createdAt", "desc"), limit(60));
            pushDocs(await getDocs(q4));
          } catch {
            const q4b = query(collection(db, "PublicTrades"), limit(60));
            pushDocs(await getDocs(q4b));
          }
        }

        const results = Array.from(resultsMap.values())
          .filter((t) => t.id !== tradeId)
          .filter(isProbablyAvailable)
          .map((t) => ({ ...t, imageURLs: normalizeImages(t) }))
          .slice(0, 12);

        if (!cancelled) setSimilarTrades(results);
      } catch (error) {
        console.error("Error fetching similar trades:", error);
        if (!cancelled) setSimilarTrades([]);
      }
    };

    fetchSimilar();
    return () => {
      cancelled = true;
    };
  }, [trade?.id, trade?.category, trade?.title]);

  const handleThumbnailClick = (index) => setMainImageIndex(index);

  const toggleFavorite = async () => {
    if (!currentUser?.uid || !trade?.id) return;

    try {
      const prefRef = doc(db, "preferences", currentUser.uid);
      const prefSnap = await getDoc(prefRef);
      const data = prefSnap.exists() ? prefSnap.data() : {};

      const favorites = Array.isArray(data.favorites) ? data.favorites : [];
      const isAlreadyFav = favorites.includes(trade.id);
      const updatedFavorites = isAlreadyFav
        ? favorites.filter((tid) => tid !== trade.id)
        : [...favorites, trade.id];

      await setDoc(prefRef, { ...data, favorites: updatedFavorites }, { merge: true });
      setIsFavorited(!isAlreadyFav);
    } catch (error) {
      console.error("❌ Failed to toggle favorite:", error);
    }
  };

  const goReport = useCallback(() => {
    navigate(`/report/${trade?.id || id}`);
  }, [navigate, trade?.id, id]);

  /* -------------------- Offer page navigation -------------------- */
  const goToOfferPage = useCallback(() => {
    if (!trade?.id) {
      toast.error("Trade not loaded yet. Please refresh and try again.");
      return;
    }

    if (!currentUser?.uid) {
      toast.info("Please log in to make an offer.");
      navigate("/login", { state: { from: `/trade/${trade.id}` } });
      return;
    }

    const requestedTradeId = normStr(trade.id);
    const requestedOwnerId = normStr(trade.ownerId || trade.ownerUid);

    if (!requestedTradeId || !requestedOwnerId) {
      toast.error("Seller not found for this trade. Please refresh and try again.");
      return;
    }

    if (requestedOwnerId === normStr(currentUser.uid)) {
      toast.error("You cannot make an offer on your own trade.");
      return;
    }

    const statePayload = {
      from: `/trade/${requestedTradeId}`,
      preferredOfferType: "cash",
      targetTrade: {
        id: requestedTradeId,
        title: trade.title || "",
        imageURLs: safeImages,
        category: trade.category || "",
        condition: trade.condition || "",
        expectedCashAmount: pickPrice(trade),
        location: trade.location || {},
        ownerId: requestedOwnerId,
      },
      qualifyingTrades: [],
    };

    navigate(OFFER_ROUTE_WITH_PARAM(requestedTradeId), { state: statePayload });
  }, [trade, currentUser?.uid, navigate, safeImages]);

  /* -------------------- Buyer process actions -------------------- */
  const callFn = async (name, payload) => {
    const fns = getFunctions(undefined, "us-central1");
    const fn = httpsCallable(fns, name);
    return await fn(payload);
  };

  const onBuyerConfirmReceipt = async () => {
    if (!trade?.id) return;
    try {
      await callFn("buyerConfirmReceipt", { tradeId: trade.id });
      toast.success("Receipt confirmed. The platform will finalize completion.");
    } catch (e) {
      toast.error(e?.message || "Failed to confirm receipt.");
    }
  };

  const onBuyerReportIssue = async () => {
    if (!trade?.id) return;

    const reasonCode = window.prompt(
      "Reason code (choose one):\nnot_received\nitem_not_as_described\ndamaged\nmissing_parts\nsuspected_counterfeit",
      "item_not_as_described"
    );
    if (!reasonCode) return;

    const details = window.prompt("Describe the issue (no contact details).", "") || "";

    try {
      await callFn("buyerReportIssue", {
        tradeId: trade.id,
        reasonCode,
        details,
        photos: [],
      });
      toast.info("Issue reported. Support will review and update you in-app.");
    } catch (e) {
      toast.error(e?.message || "Failed to report issue.");
    }
  };

  /* -------------------- Similar scroll arrows -------------------- */
  const scrollSimilar = (dir) => {
    const el = similarRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.round(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  /* -------------------- LOAD STATES -------------------- */
  if (loadState.loading) return <p>Loading trade details…</p>;

  if (loadState.accessDenied) {
    return (
      <div style={{ padding: 18 }}>
        <h3 style={{ margin: "0 0 8px" }}>Access restricted</h3>
        <p style={{ margin: 0 }}>
          This trade is currently in a controlled processing stage. Only the buyer, seller, or support can view it.
        </p>
        <div style={{ marginTop: 12 }}>
          <button
            className="offer-btn offer-btn--primary"
            type="button"
            onClick={() => navigate("/market/notifications")}
          >
            Go to Notifications
          </button>
        </div>
      </div>
    );
  }

  if (loadState.notFound || !trade) return <p>Loading trade details or item not found...</p>;

  /* -------------------- Render -------------------- */
  const isLoggedIn = !!currentUser?.uid;

  const wfStage = safeLower(normStage(workflow?.stage));

  const showBuyerVerificationPanel =
    isLoggedIn && canPartyView && isBuyer && wfStage === WF_UI.DELIVERED_PENDING_VERIFICATION;

  const showDisputeState = isLoggedIn && canPartyView && isBuyer && wfStage === WF_UI.DISPUTE_OPEN;

  const showCompletedState = isLoggedIn && canPartyView && wfStage === WF_UI.COMPLETED;

  const spApproved = safeLower(workflow?.sellerPrep?.status || "") === "approved";
  const buyerApprovalStatus = safeLower(workflow?.buyerApproval?.status || "");
  const buyerCommitted = buyerApprovalStatus === "approved";
  const buyerDeclined = buyerApprovalStatus === "declined" || buyerApprovalStatus === "rejected";

  const paymentStatus = safeLower(workflow?.payment?.status || workflow?.cash?.status || "");
  const paymentReceived = paymentStatus === "paid";

  const showBuyerCommitCard =
    isLoggedIn &&
    canPartyView &&
    isBuyer &&
    spApproved &&
    !buyerCommitted &&
    !buyerDeclined &&
    wfStage === "buyer_approval";

  const showBuyerPaymentCTA =
    isLoggedIn &&
    canPartyView &&
    isBuyer &&
    spApproved &&
    buyerCommitted &&
    !paymentReceived &&
    wfStage === "awaiting_payment";

  const tradeInProcess = !!wfStage && IN_PROCESS_STAGES.has(wfStage);

  const isOwnerViewingFreshTrade =
    isOwner &&
    !workflow &&
    !tradeInProcess;

  const showWorkflowPanel =
    isLoggedIn &&
    canPartyView &&
    !!trade?.id &&
    !!workflow &&
    tradeInProcess;

  const priceValue = moneyZAR(expectedCash) || "Price not set";

  const offerStatCardStyle = {
    padding: "14px 16px",
    borderRadius: 16,
    border: offerCountPulse ? "1px solid rgba(59,130,246,0.32)" : "1px solid rgba(15,23,42,0.08)",
    background: offerCountPulse
      ? "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(255,255,255,0.98))"
      : "linear-gradient(135deg, rgba(15,23,42,0.035), rgba(255,255,255,0.96))",
    boxShadow: offerCountPulse
      ? "0 14px 34px rgba(59,130,246,0.16)"
      : "0 10px 26px rgba(15,23,42,0.06)",
    transform: offerCountPulse ? "translateY(-1px) scale(1.01)" : "translateY(0) scale(1)",
    transition: "all 260ms ease",
  };

  const offerMicroBannerStyle = {
    marginBottom: 14,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(16,185,129,0.22)",
    background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(255,255,255,0.96))",
    color: "#065f46",
    fontWeight: 700,
    fontSize: "0.95rem",
    boxShadow: "0 12px 28px rgba(16,185,129,0.10)",
  };

  const premiumStatsGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginTop: 14,
  };

  const premiumStatStyle = {
    padding: "14px 14px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
  };

  const premiumStatLabelStyle = {
    fontSize: "0.74rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#64748b",
    marginBottom: 6,
  };

  const premiumStatValueStyle = {
    fontSize: "1.08rem",
    fontWeight: 900,
    letterSpacing: "-0.02em",
    color: "#0f172a",
  };

  return (
    <div className="trade-detail-wrapper">
      <div className="page-wrapper">
        <div className="tradeTitleBar">
          <h3 className="page-title">{trade.title || "Untitled Item"}</h3>
        </div>

        {offerMicroBanner.show && (
          <div style={offerMicroBannerStyle}>
            ✨ {offerMicroBanner.text}
          </div>
        )}

        {showThanksBanner && (
          <div className="thank-you-banner">
            ✅ Your offer was sent successfully.
          </div>
        )}

        {loadState.mode === "party" && tradeInProcess && (
          <div className="thank-you-banner" style={{ marginBottom: 12 }}>
            🔒 This trade is currently being processed by the platform.
            {getWorkflowBannerText(workflow, loadingWorkflow)}
          </div>
        )}

        {!currentUser?.uid && focusWorkflow && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Log in to continue</h4>
            <p style={{ marginTop: 6 }}>
              To approve seller preparation, pay, or track the delivery process, please log in.
            </p>

            <div className="tod-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="tod-btn tod-btn--success"
                onClick={() => navigate("/login", { state: { from: `/trade/${id}?tab=workflow` } })}
              >
                🔐 Log in
              </button>

              <button
                type="button"
                className="tod-btn"
                onClick={() => navigate("/register", { state: { from: `/trade/${id}?tab=workflow` } })}
              >
                📝 Create account
              </button>
            </div>
          </div>
        )}

        <div ref={workflowSectionRef} id="workflow" />

        {showBuyerCommitCard && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Action required: confirm trade</h4>
            <p style={{ marginTop: 6 }}>
              The platform has approved the seller evidence. Confirm to proceed to payment, or decline to stop.
            </p>

            <div className="tod-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="tod-btn tod-btn--success"
                onClick={async () => {
                  try {
                    await callFn("buyerApproveCashPrep", { tradeId: trade.id, notes: null });
                    toast.success("Confirmed. Please proceed to payment.");
                    navigate(`/checkout/${encodeURIComponent(trade.id)}`);
                  } catch (e) {
                    toast.error(e?.message || "Failed to confirm the trade.");
                  }
                }}
              >
                ✅ Confirm and proceed
              </button>

              <button
                type="button"
                className="tod-btn tod-btn--danger"
                onClick={async () => {
                  try {
                    await callFn("buyerDeclineSellerPrep", {
                      tradeId: trade.id,
                      reasonCode: "not_satisfied",
                      details: "",
                    });
                    toast.info("You declined. The trade will not proceed.");
                  } catch (e) {
                    toast.error(e?.message || "Failed to decline.");
                  }
                }}
              >
                ❌ Decline
              </button>
            </div>
          </div>
        )}

        {showBuyerPaymentCTA && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Next step: payment</h4>
            <p style={{ marginTop: 6 }}>
              Your approval is recorded. Please complete payment to allow inspection and pickup to proceed.
            </p>

            <div className="tod-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="tod-btn tod-btn--success"
                onClick={() => navigate(`/checkout/${encodeURIComponent(trade.id)}`)}
              >
                💳 Pay now
              </button>
            </div>
          </div>
        )}

        {showBuyerVerificationPanel && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Delivery verification</h4>
            <p style={{ marginTop: 6 }}>
              Please confirm the item was received in good condition. If there is an issue, report it here (no direct contact).
            </p>

            <div className="tod-actions" style={{ marginTop: 10 }}>
              <button type="button" className="tod-btn tod-btn--success" onClick={onBuyerConfirmReceipt}>
                ✅ Confirm receipt
              </button>
              <button type="button" className="tod-btn tod-btn--danger" onClick={onBuyerReportIssue}>
                ⚠️ Report an issue
              </button>
            </div>
          </div>
        )}

        {showDisputeState && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Issue reported</h4>
            <p style={{ marginTop: 6 }}>
              Your issue has been logged. Support will review and update you via in-app notifications.
            </p>
          </div>
        )}

        {showCompletedState && (
          <div className="trade-offer-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Trade completed</h4>
            <p style={{ marginTop: 6 }}>
              This trade has been finalized by the platform. Thank you for using NexterZone.
            </p>
          </div>
        )}

        {isOwnerViewingFreshTrade ? (
          <div className="trade-offer-card owner-live-card" style={{ marginBottom: 12 }}>
            <h4 className="section-title">Your trade is live</h4>
            <p style={{ marginTop: 6 }}>
              Your listing has been posted successfully and is now visible to buyers.
            </p>

            <div className="owner-live-stats" style={{ marginTop: 12 }}>
              <div className="owner-live-stat">
                <div className="owner-live-stat__label">Price</div>
                <div className="owner-live-stat__value">{priceValue}</div>
              </div>

              <div className="owner-live-stat">
                <div className="owner-live-stat__label">Views</div>
                <div className="owner-live-stat__value">{trade.viewCount ?? trade.views ?? 0}</div>
              </div>

              <div className="owner-live-stat">
                <div className="owner-live-stat__label">Offers</div>
                <div className="owner-live-stat__value">{displayOfferCount}</div>
              </div>
            </div>

            <div className="tod-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="tod-btn tod-btn--success"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                👀 View listing
              </button>

              <button
                type="button"
                className="tod-btn"
                onClick={() => navigate("/market/post-trade", { state: { postKey: Date.now() } })}
              >
                ➕ Post another trade
              </button>
            </div>
          </div>
        ) : showWorkflowPanel ? (
          <div style={{ marginBottom: 16 }}>
            <TradeWorkflowPanel
              tradeId={trade.id}
              mode={isAdmin ? "admin" : "full_workflow"}
            />
          </div>
        ) : null}

        <div className="trade-detail-container">
          <div className="left-column">
            <div className="image-preview-wrapper">
              <div className="thumbnails-left">
                {safeImages.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`Thumbnail ${index + 1}`}
                    className={`thumbnail ${index === mainImageIndex ? "active" : ""}`}
                    onClick={() => handleThumbnailClick(index)}
                    loading="lazy"
                    onError={handleImgError}
                  />
                ))}
              </div>

              <div className="main-image-container">
                <img
                  src={safeImages[mainImageIndex] || safeImages[0]}
                  alt={trade.title || "Trade"}
                  className="main-image animated"
                  onError={handleImgError}
                  loading="lazy"
                />

                {currentUser?.uid && (
                  <button
                    type="button"
                    className={`favorite-btn ${isFavorited ? "active" : ""}`}
                    onClick={toggleFavorite}
                    title={isFavorited ? "Remove from favourites" : "Add to favourites"}
                    aria-label={isFavorited ? "Remove from favourites" : "Add to favourites"}
                  >
                    {isFavorited ? "💖" : "🤍"}
                  </button>
                )}
              </div>
            </div>

            <div className="trade-details-card">
              <p className="description">{trade.description || "No description provided."}</p>

              <div className="info-row">
                📁 <strong>Category:</strong> <span>{trade.category || "N/A"}</span>
              </div>

              <div className="info-row">
                📦 <strong>Condition:</strong> <span>{trade.condition || "N/A"}</span>
              </div>

              <div className="info-row">
                📍 <strong>Location:</strong>{" "}
                <span>
                  {trade.location?.city && trade.location?.country
                    ? `${trade.location.city}, ${trade.location.country}`
                    : "Unknown"}
                </span>
              </div>

              <div className="info-row">
                📅 <strong>Posted:</strong> <span>{computePostedLabel(trade)}</span>
              </div>
            </div>
          </div>

          {isOwner ? (
            <div className="non-owner-actions">
              <div className="trade-offer-card owner-tools-card">
                <h4 className="section-title">Manage your listing</h4>

                <p style={{ marginTop: 6, opacity: 0.85 }}>
                  This item is listed for sale and visible to buyers.
                </p>

                <div className="trade-stats-box" style={{ marginTop: 14 }}>
                  <p>👁️ Views: {trade.viewCount ?? trade.views ?? 0}</p>
                  <p>🔁 Offers received: {displayOfferCount}</p>
                  <p>📅 Posted: {computePostedLabel(trade)}</p>
                  <p className="trade-stats-muted">
                    {tradeInProcess ? "A buyer process is active." : "Waiting for buyer activity."}
                  </p>
                </div>

                <div className="tod-actions" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="tod-btn tod-btn--success"
                    onClick={() => navigate("/market/post-trade", { state: { postKey: Date.now() } })}
                  >
                    ➕ Post another trade
                  </button>

                  <button
                    type="button"
                    className="tod-btn"
                    onClick={() => navigate("/market/notifications")}
                  >
                    🔔 View notifications
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="non-owner-actions">
              <div
                className="trade-offer-card"
                style={{
                  borderRadius: 22,
                  border: "1px solid rgba(15,23,42,0.08)",
                  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                  boxShadow: "0 18px 42px rgba(15,23,42,0.08)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "18px 18px 14px",
                    borderBottom: "1px solid rgba(15,23,42,0.06)",
                    background:
                      "radial-gradient(circle at top right, rgba(14,165,233,0.08), transparent 28%), linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "rgba(15,23,42,0.05)",
                      border: "1px solid rgba(15,23,42,0.08)",
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#475569",
                      marginBottom: 10,
                    }}
                  >
                    Cash purchase
                  </div>

                  <h4 className="section-title" style={{ marginBottom: 6 }}>
                    Buy this item securely
                  </h4>

                  <p style={{ margin: 0, color: "#64748b", lineHeight: 1.55 }}>
                    Submit one secure in-platform cash offer. The seller will be notified immediately.
                  </p>
                </div>

                <div style={{ padding: 18 }}>
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 18,
                      background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(255,255,255,0.98))",
                      border: "1px solid rgba(16,185,129,0.18)",
                      boxShadow: "0 12px 28px rgba(16,185,129,0.08)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#047857",
                        marginBottom: 8,
                      }}
                    >
                      Price
                    </div>

                    <div
                      style={{
                        fontSize: "1.9rem",
                        fontWeight: 950,
                        letterSpacing: "-0.04em",
                        color: "#064e3b",
                        lineHeight: 1.05,
                      }}
                    >
                      {priceValue}
                    </div>

                    {trade?.minimumCashAmount != null && trade.minimumCashAmount > 0 && (
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: "#059669",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Minimum: {moneyZAR(trade.minimumCashAmount)}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 8,
                        fontSize: "0.92rem",
                        color: "#065f46",
                      }}
                    >
                      Protected buyer-to-seller offer flow
                    </div>
                  </div>

                  <div style={premiumStatsGridStyle}>
                    <div style={premiumStatStyle}>
                      <div style={premiumStatLabelStyle}>Views</div>
                      <div style={premiumStatValueStyle}>{trade.viewCount ?? trade.views ?? 0}</div>
                    </div>

                    <div style={offerStatCardStyle}>
                      <div style={premiumStatLabelStyle}>Offers made</div>
                      <div style={premiumStatValueStyle}>{displayOfferCount}</div>
                    </div>

                    <div style={premiumStatStyle}>
                      <div style={premiumStatLabelStyle}>Messages</div>
                      <div style={premiumStatValueStyle}>{trade.messageCount ?? 0}</div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      padding: "12px 14px",
                      borderRadius: 14,
                      background: "rgba(15,23,42,0.03)",
                      border: "1px solid rgba(15,23,42,0.06)",
                      color: "#475569",
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                    }}
                  >
                    Secure in-platform cash offers only. No direct payment or contact details are shared here.
                  </div>

                  {currentUser?.uid ? (
                    <div style={{ marginTop: 16 }}>
                      <TradePerformanceChart tradeId={trade.id} mode="insights" />
                    </div>
                  ) : null}

                  <div className="tod-actions" style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="tod-btn tod-btn--success"
                      onClick={goToOfferPage}
                      disabled={tradeInProcess}
                      title={tradeInProcess ? "Trade is already in process" : ""}
                      style={{
                        width: "100%",
                        minHeight: 48,
                        fontSize: "0.98rem",
                        fontWeight: 900,
                        boxShadow: tradeInProcess
                          ? "none"
                          : "0 14px 30px rgba(16,185,129,0.16)",
                      }}
                    >
                      {tradeInProcess ? "🔒 Trade in process" : "💸 Send cash offer"}
                    </button>
                  </div>

                  <div className="reportRow" style={{ marginTop: 14 }}>
                    <button
                      type="button"
                      className="reportActionBtn"
                      onClick={goReport}
                      title="Report this listing"
                      aria-label="Report this listing"
                    >
                      <span className="reportActionBtn__icon">⚠️</span>
                      <span className="reportActionBtn__text">Report</span>
                      <span className="reportRow__hint">Helps keep NexterZone safe.</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {(currentUser?.uid || (!HIDE_SIMILAR_ITEMS && similarTrades.length > 0)) && (
          <div className="trade-detail-full-width">
            {currentUser?.uid ? (
              <div style={{ marginTop: 18 }}>
                <SimilarTradesCarousel
                  currentTrade={trade}
                  uid={currentUser?.uid || null}
                  maxSimilar={10}
                  maxRecommended={12}
                  title="More options"
                  subtitle="Explore similar listings and picks tailored to you"
                  preferSameCity={true}
                />
              </div>
            ) : null}

            {!HIDE_SIMILAR_ITEMS && similarTrades.length > 0 && (
              <div className="sim-shelf">
                <div className="sim-shelf__head">
                  <div>
                    <div className="sim-shelf__title">More options like this</div>
                    <div className="sim-shelf__sub">
                      Similar listings in {trade.category || "this category"}
                    </div>
                  </div>

                  <div className="sim-shelf__controls">
                    <button
                      type="button"
                      className="sim-nav"
                      onClick={() => scrollSimilar("left")}
                      aria-label="Scroll left"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="sim-nav"
                      onClick={() => scrollSimilar("right")}
                      aria-label="Scroll right"
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="sim-strip" ref={similarRef}>
                  {similarTrades.map((item) => {
                    const price = moneyZAR(pickPrice(item));
                    const city = item.location?.city || "Unknown";
                    const country = item.location?.country || "";
                    const loc = country ? `${city}, ${country}` : city;

                    return (
                      <Link key={item.id} to={`/trade/${item.id}`} className="sim-card">
                        <div className="sim-card__imgWrap">
                          <img
                            src={getTradeImageSrc(item)}
                            alt={item.title || "Similar trade"}
                            loading="lazy"
                            onError={handleImgError}
                          />
                          {price ? <div className="sim-card__badge">{price}</div> : null}
                        </div>

                        <div className="sim-card__body">
                          <div className="sim-card__title" title={item.title || ""}>
                            {item.title || "Untitled"}
                          </div>

                          <div className="sim-card__meta">
                            <span className="sim-tag">{item.condition || "Condition N/A"}</span>
                            <span className="sim-dot">•</span>
                            <span className="sim-loc">{loc}</span>
                          </div>

                          <div className="sim-card__foot">
                            <span className="sim-muted">View</span>
                            <span className="sim-cta">→</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeDetail;